// The one place a top-up starts, wherever it starts from: the paywall (a tap
// was blocked), the balance chip, or the account page.
//
//   Buy credits
//   This needs 600 credits; you have 350.        (only when a tap is blocked)
//   1,500 credits · $45   covers this   [Buy]
//   5,000 credits · $129                [Buy]
//   12,000 credits · $279               [Buy]
//   Pack credits are good for 12 months.
//   or change plan                                      [Not now]
//
// One tap on a row starts the Stripe checkout (POST /credits/checkout/) and
// stashes where to come back to — and what the agent was about to do — so
// /success?purchase=pack returns to the exact page and offers the interrupted
// action again. Packs come from GET /credits/packs/?need=<shortfall> (the
// server marks the one it recommends); until that answers, or if it fails,
// the local catalog stands in and the smallest pack that covers the
// shortfall is the one marked. No urgency, no arithmetic about what she
// "could still afford".

import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { API_URL } from "../../constants";
import {
  CREDITS_PACK_KEY,
  PACKS,
  PACK_EXPIRY_MONTHS,
  PACK_KEYS,
  formatCredits,
  formatDollars,
  stashCreditsReturn,
} from "../../constants/credits";
import { authHeader } from "../../utils/auth";
import { storageSet } from "../../utils/storage";
import CostLine from "./CostLine";
import Sheet, { SheetActions, SheetError, SheetSecondary, SheetTitle } from "./Sheet";

/** @typedef {import("../../constants/credits").CreditsReturn} CreditsReturn */
/** @typedef {{ key: string, credits: number, amountCents: number, recommended: boolean }} PackRow */

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid var(--brand-border, #e3ebec);

  &:last-of-type {
    border-bottom: 1px solid var(--brand-border, #e3ebec);
  }
`;

const RowText = styled.div`
  font-size: 14px;
  color: var(--brand-ink, #0b1c1e);
  line-height: 1.4;

  span {
    color: var(--brand-ink-3, #5c7074);
  }
`;

const Covers = styled.em`
  display: inline-block;
  margin-left: 8px;
  font-style: normal;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--brand-primary-700, #085552);
`;

const RowButton = styled.button`
  background: none;
  border: 1px solid var(--brand-primary, #0e7e7a);
  color: var(--brand-primary-700, #085552);
  border-radius: 999px;
  padding: 7px 16px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: var(--brand-primary-50, #e6f3f2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Note = styled.p`
  font-size: 12.5px;
  color: var(--brand-ink-4, #8a9a9d);
  margin: 8px 0 0;
`;

const PlanLink = styled(Link)`
  font-size: 13.5px;
  color: var(--brand-ink-3, #5c7074) !important;
  text-decoration: underline;
  margin-right: auto;

  &[aria-disabled="true"] {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/** @param {unknown} err @param {string} fallback */
export const checkoutErrorText = (err, fallback) => {
  const e = /** @type {{ response?: { status?: number, data?: { error?: string } } }} */ (err);
  const status = e?.response?.status;
  if (status === 401) return "Your session expired. Sign in again to continue.";
  if (status === 429) return "Too many attempts. Wait a minute, then try again.";
  return e?.response?.data?.error || fallback;
};

/**
 * The local catalog as rows, with the smallest pack that covers the
 * shortfall marked. Nothing is marked when nothing is short.
 * @param {number} need
 * @returns {PackRow[]}
 */
export const localPackRows = (need) => {
  const rows = PACK_KEYS.map((key) => ({
    key,
    credits: PACKS[key].credits,
    amountCents: PACKS[key].amountCents,
    recommended: false,
  }));
  if (need > 0) {
    const covering = rows.find((row) => row.credits >= need);
    if (covering) covering.recommended = true;
  }
  return rows;
};

/** @param {any} data The API's `{ packs: [...] }`. @returns {PackRow[] | null} */
const rowsFromApi = (data) => {
  const packs = Array.isArray(data?.packs) ? data.packs : null;
  if (!packs || packs.length === 0) return null;
  /** @type {PackRow[]} */
  const rows = [];
  for (const pack of packs) {
    const credits = Number(pack?.credits);
    const amountCents = Number(pack?.amount_cents);
    if (typeof pack?.key !== "string" || !Number.isFinite(credits) || !Number.isFinite(amountCents))
      continue;
    rows.push({ key: pack.key, credits, amountCents, recommended: pack.recommended === true });
  }
  return rows.length ? rows : null;
};

/** The covering pack first, then the rest in catalog order. @param {PackRow[]} rows */
const orderRows = (rows) => [
  ...rows.filter((row) => row.recommended),
  ...rows.filter((row) => !row.recommended),
];

/**
 * The pack rows and the checkout tap, without any sheet chrome — so the
 * paywall sheet can compose them under its own title and plan section.
 *
 * @param {{
 *   need?: number,
 *   returnStash: CreditsReturn,
 *   onBusyChange?: (busy: boolean) => void,
 *   disabled?: boolean,
 *   checkoutLock?: React.MutableRefObject<boolean>,
 * }} props
 *   need: the shortfall in credits (0 or omitted when nothing is blocked).
 *   returnStash: written at the tap — where /success and /cancel return to,
 *   and the interrupted action to offer again.
 */
export const TopUpPacks = ({
  need = 0,
  returnStash,
  onBusyChange,
  disabled = false,
  checkoutLock,
}) => {
  const shortfall = Number.isFinite(need) && need > 0 ? Math.ceil(need) : 0;
  const [rows, setRows] = useState(() => localPackRows(shortfall));
  const [busyKey, setBusyKey] = useState(/** @type {string | null} */ (null));
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const localCheckoutLock = useRef(false);
  const lock = checkoutLock || localCheckoutLock;
  const mountedRef = useRef(false);
  const onBusyChangeRef = useRef(onBusyChange);
  onBusyChangeRef.current = onBusyChange;
  const busy = disabled || busyKey !== null;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // The server's recommendation, when it answers; the local catalog otherwise.
  useEffect(() => {
    let cancelled = false;
    const params = shortfall ? `?need=${encodeURIComponent(shortfall)}` : "";
    axios
      .get(`${API_URL}/credits/packs/${params}`, { headers: authHeader() })
      .then((resp) => {
        const fromApi = rowsFromApi(resp?.data);
        if (!cancelled && fromApi) setRows(fromApi);
      })
      .catch(() => {
        /* the local catalog is already showing */
      });
    return () => {
      cancelled = true;
    };
  }, [shortfall]);

  const ordered = useMemo(() => orderRows(rows), [rows]);

  const buyPack = useCallback(
    /** @param {string} packKey */ async (packKey) => {
      if (!mountedRef.current || disabled || lock.current) return;
      // Shared with plan checkout: React state alone cannot guard same-tick taps.
      lock.current = true;
      onBusyChangeRef.current?.(true);
      setBusyKey(packKey);
      setError(null);
      // Come back to where this started, whichever way Stripe ends.
      stashCreditsReturn(returnStash);
      try {
        const resp = await axios.post(
          `${API_URL}/credits/checkout/`,
          { pack: packKey },
          { headers: authHeader(), timeout: 30000 }
        );
        if (!mountedRef.current) return;
        if (resp.data?.stripe_url) {
          storageSet(CREDITS_PACK_KEY, packKey);
          window.location.href = resp.data.stripe_url;
          return;
        }
        setError("We couldn't start checkout. Please try again in a moment.");
      } catch (err) {
        if (!mountedRef.current) return;
        setError(
          checkoutErrorText(err, "We couldn't start checkout. Please try again in a moment.")
        );
      }
      lock.current = false;
      setBusyKey(null);
      onBusyChangeRef.current?.(false);
    },
    [disabled, lock, returnStash]
  );

  return (
    <div data-testid="top-up-packs">
      {ordered.map((row) => (
        <Row key={row.key} data-testid="pack-row">
          <RowText>
            {formatCredits(row.credits)} <span>· {formatDollars(row.amountCents)}</span>
            {shortfall > 0 && row.recommended && <Covers>covers this</Covers>}
          </RowText>
          <RowButton
            type="button"
            onClick={() => buyPack(row.key)}
            disabled={busy}
            aria-label={`Buy ${formatCredits(row.credits)}`}
          >
            {busyKey === row.key ? "Opening…" : "Buy"}
          </RowButton>
        </Row>
      ))}
      <Note>Pack credits are good for {PACK_EXPIRY_MONTHS} months.</Note>
      {error && <SheetError role="alert">{error}</SheetError>}
    </div>
  );
};

/**
 * @param {{
 *   onClose: () => void,
 *   returnFocusRef?: React.RefObject<HTMLElement>,
 *   need?: number,
 *   balance?: number | null,
 *   returnPath?: string,
 *   listingUuid?: string,
 *   resume?: import("../../constants/credits").CreditsResume,
 * }} props
 *   need: the credits the blocked tap needs (its full cost); omit or 0 when
 *   nothing is blocked — then there is no shortfall line, just the packs.
 *   balance: what she has, for the fact line (null = unknown).
 *   returnPath: where the Stripe legs come back to; defaults to here.
 *   listingUuid / resume: ride the return stash so the page can offer the
 *   interrupted action again.
 */
const TopUpSheet = ({
  onClose,
  returnFocusRef,
  need = 0,
  balance,
  returnPath,
  listingUuid,
  resume,
}) => {
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const checkoutLock = useRef(false);
  const close = useCallback(() => {
    if (!checkoutLock.current) onClose();
  }, [onClose]);
  const blocked = need > 0 && typeof balance === "number" && need > balance;
  const shortfall = blocked ? need - /** @type {number} */ (balance) : 0;
  const returnStash = useMemo(
    () => ({
      path: returnPath || `${location.pathname}${location.search}`,
      ...(listingUuid ? { listingUuid } : {}),
      ...(resume ? { resume } : {}),
    }),
    [returnPath, listingUuid, resume, location.pathname, location.search]
  );

  return (
    <Sheet
      labelledBy="top-up-title"
      onClose={busy ? undefined : close}
      returnFocusRef={returnFocusRef}
    >
      <SheetTitle id="top-up-title">Buy credits</SheetTitle>
      {blocked && <CostLine cost={need} balance={balance} blocked />}
      <TopUpPacks
        need={shortfall}
        returnStash={returnStash}
        onBusyChange={setBusy}
        checkoutLock={checkoutLock}
        disabled={busy}
      />
      <SheetActions>
        {busy ? (
          // biome-ignore lint/a11y/useSemanticElements: links have no native disabled state; the placeholder must not retain a navigable href during checkout.
          <PlanLink as="span" role="link" aria-disabled="true" tabIndex={-1}>
            or change plan
          </PlanLink>
        ) : (
          <PlanLink
            to="/pricing"
            onClick={(event) => {
              if (checkoutLock.current) event.preventDefault();
            }}
          >
            or change plan
          </PlanLink>
        )}
        <SheetSecondary type="button" onClick={close} disabled={busy}>
          Not now
        </SheetSecondary>
      </SheetActions>
    </Sheet>
  );
};

export default TopUpSheet;
